import { DialogContent, DialogOverlay } from '@reach/dialog'
import { Icon } from '~/elements/Icons/Icon'
import { Button } from '~/elements/Button/Button'
import { usePreviewMode } from '~/hooks/usePreviewMode'
import { createGraphQLClient } from '~/graphql/contentfulClient'
import { RTEParser } from '~/lib/RTEParser/RTEParser'
import { useRouter } from 'next/router'
import { DEFAULT_LOCALE } from 'config/constants'
import { useNavigationQuery } from '@contentfulTypes'
import { useDevice } from '~/hooks/useDevice'
import { RteRteLinks } from '@contentfulTypes'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'

type Props = {
  sku: string
  uuid: string
  open: boolean
  close: () => void
}

const setupHref = (props: any) => {
  let href = ''

  if (useDevice.IS_AR_QUICKLOOK_CANDIDATE) {
    const {
      iosSrc,
      applePayButtonType,
      checkoutTitle,
      checkoutSubtitle,
      price,
      callToAction,
      customBanner,
      customHeight,
      noScale,
    } = props

    href = `${iosSrc}#`

    if (applePayButtonType) href += `&applePayButtonType=${encodeURIComponent(applePayButtonType)}`

    if (checkoutTitle) href += `&checkoutTitle=${encodeURIComponent(checkoutTitle)}`

    if (checkoutSubtitle) href += `&checkoutSubtitle=${encodeURIComponent(checkoutSubtitle)}`

    if (price) href += `&price=${encodeURIComponent(price)}`

    if (callToAction) href += `&callToAction=${encodeURIComponent(callToAction)}`

    if (customBanner) href += `&custom=${encodeURIComponent(customBanner)}`

    if (customHeight) href += `&customHeight=${encodeURIComponent(customHeight)}`

    if (noScale != null) href += `&allowsContentScaling=0`
  } else if (useDevice.IS_SCENEVIEWER_CANDIDATE) {
    const { src, title, fallbackUrl, link, noScale } = props

    href = `intent://arvr.google.com/scene-viewer/1.0?file=${src}&mode=ar_only`
    if (title) href += `&title=${encodeURIComponent(title)}`

    if (link) href += `&link=${encodeURIComponent(link)}`

    if (noScale != null) href += `&resizable=false`

    href +=
      `#Intent;scheme=https;` + `package=com.google.ar.core;` + `action=android.intent.action.VIEW;`
    if (fallbackUrl) href += `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`

    href += `end;`
  }

  return href
}

export const AugmentedRealityPopup = ({ sku, uuid, open, close }: Props) => {
  const preview = usePreviewMode()
  const contentfulClient = createGraphQLClient({ preview })
  const router = useRouter()
  const { data } = useNavigationQuery(contentfulClient, {
    preview,
    locale: router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE,
  })

  const popupData = data?.pageCollection?.items[0]?.productAugmentedReality

  if (!popupData) return null

  const handlePoupupButton = () => {
    close()
    const anchor = document.createElement('a')
    const href = setupHref({
      src: `https://viewer.cgtarsenal.com/benuta_gmbh/${uuid}/${sku}.glb`,
      iosSrc: `https://viewer.cgtarsenal.com/benuta_gmbh/${uuid}/${sku}.usdz`,
    })
    if (useDevice.IS_AR_QUICKLOOK_CANDIDATE) {
      anchor.appendChild(document.createElement('img'))
      anchor.rel = 'ar'
    }
    anchor.setAttribute('href', href)
    anchor.click()
  }

  return (
    <DialogOverlay
      style={{
        width: '100vw',
        height: '100vh',
        top: 0,
        left: 0,
        zIndex: 9999,
        padding: 0,
        margin: 0,
      }}
      isOpen={open}
      onDismiss={close}
    >
      <DialogContent
        style={{
          position: 'relative',
          width: '94vw',
        }}
      >
        <>
          <button
            className="fixed top-4 right-4 z-50 grid h-10 w-10 place-items-center rounded-full bg-grey-light"
            onClick={close}
          >
            <Icon name="cross" />
          </button>
          <div className="text-center text-h2-mobile font-bold">{popupData?.headline}</div>
          <div className="my-8 text-p-normal">
            {popupData?.rte ? (
              <RTEParser
                rteJSONContent={popupData?.rte?.json}
                links={popupData?.rte?.links as RteRteLinks}
              />
            ) : null}
          </div>
          <Button ctaButtonStyle="Main" type="button" fullWidth onClick={handlePoupupButton}>
            <div className="flex items-center justify-center">
              <Icon name="augmented-reality-cube" />
              <span className="ml-2">{popupData?.ctaText}</span>
            </div>
          </Button>
        </>
      </DialogContent>
    </DialogOverlay>
  )
}
